import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { MatButton } from '@angular/material/button'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderNumberCellDirective,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  ElderToolbarContentDirective,
  FilterUtil,
  IDataContext,
  IsoDurationPipe,
  LocalListDataSource,
  Sort,
  SortUtil,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { WorkItem } from '../work-item'
import { ScheduleMismatchReason, WorkItemSchedule } from '../work-item-schedule'
import { WorkItemScheduleComponent } from '../work-item-schedule/work-item-schedule.component'
import { RouteEvalOptionsBuilder, WorkItemSchedulerService } from '../work-item-scheduler.service'

@Component({
  selector: 'app-schedule-select-route-table',
  templateUrl: './schedule-select-route-table.component.html',
  styleUrls: ['./schedule-select-route-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    ElderNumberCellDirective,
    MatSortHeader,
    MatCellDef,
    MatCell,
    WorkItemScheduleComponent,
    MatButton,
    AsyncPipe,
    ElderTranslatePipe,
    IsoDurationPipe,
  ],
})
export class ScheduleSelectRouteTableComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  /**
   * Current work items to take into consideration
   */
  private readonly _items$ = new BehaviorSubject<WorkItem[]>([])

  public readonly showAllRoutes$ = new BehaviorSubject<boolean>(false)

  /**
   * The route that has been chosen for the items in question
   */
  public readonly chosenRoute$ = new BehaviorSubject<WorkItemSchedule>(null)

  protected readonly data: IDataContext<WorkItemSchedule>
  private readonly dataSource: LocalListDataSource<WorkItemSchedule>
  private workSchedulerService = inject(WorkItemSchedulerService)
  private toastService = inject(ElderToastService)

  /**
   * The ids of the modified items
   */
  @Output()
  public itemsModifiedIds: EventEmitter<string[]> = new EventEmitter<string[]>()

  @Input()
  public set items(items: WorkItem[]) {
    this._items$.next(items)
  }

  @Input()
  public set showAllRoutes(showAllRoutes: boolean) {
    this.showAllRoutes$.next(showAllRoutes)
  }

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.dataSource = LocalListDataSource.empty<WorkItemSchedule>(
      'path', // guaranteed to be unique
      SortUtil.sortData,
      FilterUtil.filterData
    )
    this.data = DataContextBuilder.start<WorkItemSchedule>()
      .autoStartAsap([new Sort('workCenterNumberSequence', 'asc')])
      .reloadOnLocalChanges()
      .build(this.dataSource)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this._items$.subscribe(() => this.reloadTable(this.showAllRoutes))
    this.showAllRoutes$.subscribe((showAll) => this.reloadTable(showAll))
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get showAllRoutes(): boolean {
    return this.showAllRoutes$.getValue()
  }

  public get items(): WorkItem[] {
    return this._items$.getValue()
  }

  private get chosenRoute(): WorkItemSchedule {
    return this.chosenRoute$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onRouteSelected(route: WorkItemSchedule): void {
    this.chosenRoute$.next(route)
  }

  public showMakespan(route: WorkItemSchedule): boolean {
    // TODO maybe rework?
    // We cannot calculate the makespan, if the work center is not scheduled on this day
    return (
      route &&
      !route.segments.some((s) =>
        s?.matchResult?.mismatchReasons?.includes(ScheduleMismatchReason.WORK_HOURS)
      )
    )
  }

  public onSaveRoutes() {
    const workItems = this.items
    const route = this.chosenRoute
    if (workItems && workItems.length > 0 && route) {
      this.saveRoutes(workItems, route)
    }
  }

  public notAlreadySelected: (route: WorkItemSchedule) => boolean = (route) =>
    !this.items.map((item) => item?.productionRoute?.productionPath ?? '').includes(route.path)

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  /**
   * Loads all possible routes for the given items.
   * @param ignoreConstraints Whether routes should be shown, that are not determined possible.
   */
  private loadRoutes(ignoreConstraints: boolean): Observable<WorkItemSchedule[]> {
    const filters = ignoreConstraints ? RouteEvalOptionsBuilder.ignoreAll() : []
    return this.workSchedulerService.findAggregatedRoutesForAll(this.items, filters)
  }

  /**
   * Reload possible routes, refresh table.
   * @param ignoreConstraints Whether routes should be shown, that are not determined possible.
   */
  private reloadTable(ignoreConstraints: boolean) {
    if (this.items.length > 0) {
      this.loadRoutes(ignoreConstraints).subscribe({
        next: (routes) => (this.dataSource.data = routes),
        error: (err) => this.toastService.error('Failed to load production routes!', err),
      })
    } else {
      this.dataSource.data = []
    }
  }

  private saveRoutes(items: WorkItem[], route: WorkItemSchedule): void {
    this.workSchedulerService.assignAggregatedRouteForAll(items, route).subscribe({
      next: () => {
        this.itemsModifiedIds.emit(items.map((i) => i.id))
        this.toastService.info('Saving successful')
      },
      error: (err) => this.toastService.error('Failed to assign new production routes!', err),
    })
  }
}
